import { useEffect, useRef, useState } from 'react';
import { matchesStreamChannel } from './channel.js';
import { classifyStreamHealth, IDLE_STREAM_HEALTH } from './health.js';
import { closePeerConnection, createLivePeerConnection, toRtcIceCandidateInit } from './peer.js';
import { deriveLinkRates, readPeerLinkCounters } from './stats.js';
const HEALTH_POLL_MS = 2_000;
const HEALTH_SEND_MS = 8_000;
export function useLiveSubscriber({ enabled, showId, localPeerId, sendSignal, subscribe, channel = 'show', }) {
    const [remoteStream, setRemoteStream] = useState(null);
    const [health, setHealth] = useState(IDLE_STREAM_HEALTH);
    const peerRef = useRef(null);
    const sendRef = useRef(sendSignal);
    sendRef.current = sendSignal;
    const localPeerIdRef = useRef(localPeerId);
    localPeerIdRef.current = localPeerId;
    const publisherPeerIdRef = useRef(null);
    const lastHealthSentRef = useRef({ key: '', at: 0 });
    useEffect(() => {
        if (!enabled || !showId || !localPeerId) {
            closePeerConnection(peerRef.current ?? undefined);
            peerRef.current = null;
            publisherPeerIdRef.current = null;
            setRemoteStream(null);
            setHealth(IDLE_STREAM_HEALTH);
            return;
        }
        const currentShowId = showId;
        let prevCounters = null;
        let prevAt = 0;
        let connectedSince = 0;
        const hello = () => {
            sendRef.current({ showId: currentShowId, kind: 'viewer.hello', channel });
        };
        const pendingIceRef = { current: [] };
        const unsubscribe = subscribe((signal) => {
            if (signal.showId !== currentShowId || signal.fromPeerId === localPeerIdRef.current) {
                return;
            }
            if (!matchesStreamChannel(signal, channel)) {
                return;
            }
            if (signal.kind === 'publisher.ready') {
                hello();
                return;
            }
            if (signal.kind === 'offer' && signal.sdp && (!signal.toPeerId || signal.toPeerId === localPeerIdRef.current)) {
                closePeerConnection(peerRef.current ?? undefined);
                pendingIceRef.current = [];
                publisherPeerIdRef.current = signal.fromPeerId;
                const peer = createLivePeerConnection();
                peerRef.current = peer;
                peer.ontrack = (event) => {
                    const stream = event.streams[0] ?? new MediaStream([event.track]);
                    setRemoteStream(stream);
                };
                peer.onicecandidate = (event) => {
                    if (!event.candidate) {
                        return;
                    }
                    sendRef.current({
                        showId: currentShowId,
                        kind: 'ice',
                        toPeerId: signal.fromPeerId,
                        channel,
                        candidate: {
                            candidate: event.candidate.candidate,
                            sdpMid: event.candidate.sdpMid,
                            sdpMLineIndex: event.candidate.sdpMLineIndex,
                            usernameFragment: event.candidate.usernameFragment,
                        },
                    });
                };
                peer.onconnectionstatechange = () => {
                    if (peer.connectionState === 'failed' || peer.connectionState === 'closed') {
                        setRemoteStream(null);
                    }
                };
                void (async () => {
                    await peer.setRemoteDescription({ type: 'offer', sdp: signal.sdp ?? '' });
                    for (const candidate of pendingIceRef.current) {
                        await peer.addIceCandidate(toRtcIceCandidateInit(candidate));
                    }
                    pendingIceRef.current = [];
                    const answer = await peer.createAnswer();
                    await peer.setLocalDescription(answer);
                    const sdp = peer.localDescription?.sdp;
                    if (!sdp) {
                        return;
                    }
                    sendRef.current({
                        showId: currentShowId,
                        kind: 'answer',
                        toPeerId: signal.fromPeerId,
                        channel,
                        sdp,
                    });
                })();
                return;
            }
            if (signal.kind === 'ice' && signal.candidate) {
                const peer = peerRef.current;
                if (!peer || peer.remoteDescription === null) {
                    pendingIceRef.current.push(signal.candidate);
                    return;
                }
                void peer.addIceCandidate(toRtcIceCandidateInit(signal.candidate));
                return;
            }
            if (signal.kind === 'peer.left') {
                closePeerConnection(peerRef.current ?? undefined);
                peerRef.current = null;
                publisherPeerIdRef.current = null;
                setRemoteStream(null);
            }
        });
        const pollHealth = async () => {
            const peer = peerRef.current;
            if (!peer) {
                setHealth((current) => current.severity === 'idle' && current.connectedPeers === 0 ? current : IDLE_STREAM_HEALTH);
                return;
            }
            const now = Date.now();
            if (peer.connectionState === 'connected') {
                if (connectedSince === 0) {
                    connectedSince = now;
                }
            }
            else {
                connectedSince = 0;
            }
            const counters = await readPeerLinkCounters(peer, 'inbound');
            const rates = deriveLinkRates(prevCounters, counters, prevAt ? now - prevAt : 0);
            prevCounters = counters;
            prevAt = now;
            const receivers = peer.getReceivers();
            const hasExpectedVideo = receivers.some((receiver) => receiver.track?.kind === 'video' && receiver.track.readyState === 'live');
            const hasExpectedAudio = receivers.some((receiver) => receiver.track?.kind === 'audio' && receiver.track.readyState === 'live');
            const classified = classifyStreamHealth({
                side: 'subscriber',
                warmupDone: connectedSince > 0 && now - connectedSince > 4_000,
                connectedPeers: peer.connectionState === 'connected' ? 1 : 0,
                connectionFailed: peer.connectionState === 'failed' || peer.connectionState === 'disconnected',
                hasExpectedVideo,
                hasExpectedAudio,
                videoBitrateKbps: rates.videoBitrateKbps,
                audioBitrateKbps: rates.audioBitrateKbps,
                videoFps: rates.videoFps,
                packetLossPct: rates.packetLossPct,
                rttMs: counters.rttMs,
            });
            const next = {
                ...IDLE_STREAM_HEALTH,
                severity: classified.severity === 'ok' && peer.connectionState !== 'connected' ? 'idle' : classified.severity,
                issues: classified.issues,
                bitrateKbps: rates.videoBitrateKbps,
                rttMs: counters.rttMs,
                packetLossPct: rates.packetLossPct,
                connectedPeers: peer.connectionState === 'connected' ? 1 : 0,
            };
            setHealth((current) => {
                if (current.severity === next.severity &&
                    current.connectedPeers === next.connectedPeers &&
                    current.issues.join(',') === next.issues.join(',')) {
                    return current;
                }
                return next;
            });
            const publisherPeerId = publisherPeerIdRef.current;
            if (!publisherPeerId) {
                return;
            }
            const key = classified.issues.join(',');
            const last = lastHealthSentRef.current;
            const due = now - last.at >= HEALTH_SEND_MS;
            if (key === last.key && !due) {
                return;
            }
            if (key === '' && last.key === '') {
                return;
            }
            lastHealthSentRef.current = { key, at: now };
            sendRef.current({
                showId: currentShowId,
                kind: 'health',
                toPeerId: publisherPeerId,
                channel,
                health: {
                    issues: classified.issues,
                    bitrateKbps: rates.videoBitrateKbps,
                    rttMs: counters.rttMs,
                    packetLossPct: rates.packetLossPct,
                },
            });
        };
        const helloTimer = window.setTimeout(hello, 250);
        const retryId = window.setInterval(() => {
            if (!peerRef.current) {
                hello();
            }
        }, 8_000);
        const pollId = window.setInterval(() => {
            void pollHealth();
        }, HEALTH_POLL_MS);
        return () => {
            window.clearTimeout(helloTimer);
            window.clearInterval(retryId);
            window.clearInterval(pollId);
            unsubscribe();
            closePeerConnection(peerRef.current ?? undefined);
            peerRef.current = null;
            publisherPeerIdRef.current = null;
            setRemoteStream(null);
            setHealth(IDLE_STREAM_HEALTH);
        };
    }, [enabled, showId, localPeerId, subscribe, channel]);
    return {
        remoteStream,
        connecting: enabled && remoteStream === null,
        health,
    };
}
//# sourceMappingURL=useLiveSubscriber.js.map